import { Injectable } from '@angular/core';
import { SelectListOption, SuggestionItem } from '@bmw-ds/components';

import { SuggestedAddress } from '../../clients/tolerant-post/model/view.model';
import { AddressField } from '../model/address-field.model';
import { CustomerAddress } from '../model/customer-information.model';
import { PROVINCE_NAMES } from '../model/province-names.model';

export interface SuggestionOfAddressField {
  suggestion: SuggestionItem;
  addressField: AddressField;
}

enum AddressVariant {
  HOUSE_NUMBER_BEFORE_STREET = 'HOUSE_NUMBER_BEFORE_STREET',
  POSTAL_CODE_AFTER_CITY = 'POSTAL_CODE_AFTER_CITY',
}

@Injectable({
  providedIn: 'root',
})
export class AddressViewService {
  private static marketAddressVariants: Record<AddressVariant, Set<string>> = {
    [AddressVariant.HOUSE_NUMBER_BEFORE_STREET]: new Set(['GB', 'FR', 'IE', 'LU', 'MC']),
    [AddressVariant.POSTAL_CODE_AFTER_CITY]: new Set(['GB', 'IE']),
  };

  private marketUsesAddressVariant(variant: AddressVariant, market: string | undefined): boolean {
    return !!market && AddressViewService.marketAddressVariants[variant].has(market?.toUpperCase());
  }

  private getStreetLineForMarket(market: string | undefined) {
    if (this.marketUsesAddressVariant(AddressVariant.HOUSE_NUMBER_BEFORE_STREET, market)) {
      return [AddressField.HOUSE_NUMBER, AddressField.STREET];
    } else {
      return [AddressField.STREET, AddressField.HOUSE_NUMBER];
    }
  }

  private getCityLinesForMarket(market: string | undefined, formMode: boolean) {
    if (!this.marketUsesAddressVariant(AddressVariant.POSTAL_CODE_AFTER_CITY, market)) {
      return [[AddressField.POSTAL_CODE, AddressField.CITY]];
    } else if (formMode) {
      return [[AddressField.CITY, AddressField.POSTAL_CODE]];
    } else {
      return [[AddressField.CITY], [AddressField.POSTAL_CODE]];
    }
  }

  private getAdditionalAddressLines() {
    return [[AddressField.ADDRESS_LINE_1], [AddressField.ADDRESS_LINE_2]];
  }

  private getFormFormatForMarket(market: string | undefined, includeOptionalAddressFields = true): AddressField[][] {
    return [
      ...this.getCityLinesForMarket(market, true),
      this.getStreetLineForMarket(market),
      ...(includeOptionalAddressFields ? [...this.getAdditionalAddressLines(), [AddressField.POST_BOX]] : []),
    ];
  }

  private getPrintFormatForMarket(
    market: string | undefined,
    includeOptionalAddressFields = true,
    includeProvince = true,
  ): AddressField[][] {
    return [
      ...(includeOptionalAddressFields ? [...this.getAdditionalAddressLines(), [AddressField.POST_BOX]] : []),
      this.getStreetLineForMarket(market),
      ...this.getCityLinesForMarket(market, false),
      ...(includeProvince ? [[AddressField.PROVINCE]] : []),
    ];
  }

  getEditCustomerUIFormatForMarket(market: string | undefined): AddressField[][] {
    return [[AddressField.COUNTRY], ...this.getFormFormatForMarket(market), [AddressField.PROVINCE]];
  }

  getAdvancedSearchUIFormatForMarket(market: string | undefined): AddressField[][] {
    return this.getFormFormatForMarket(market, false);
  }

  getCustomerAddressFieldValue(field: AddressField, address: CustomerAddress): string | undefined {
    switch (field) {
      case AddressField.POSTAL_CODE:
        return address.postalCode;
      case AddressField.CITY:
        return address.city;
      case AddressField.HOUSE_NUMBER:
        return address.houseNumber;
      case AddressField.STREET:
        return address.street;
      case AddressField.PROVINCE:
        return this.getProvinceName(address.province, address.country);
      case AddressField.COUNTRY:
        return address.country;
      case AddressField.ADDRESS_LINE_1:
        return address.addressLine1;
      case AddressField.ADDRESS_LINE_2:
        return address.addressLine2;
      case AddressField.POST_BOX:
        return address.postBox;
    }
  }

  getSuggestedAddressUiFieldValue(field: AddressField, address: SuggestedAddress): string | undefined {
    switch (field) {
      case AddressField.POSTAL_CODE:
        return address.postalCode;
      case AddressField.CITY:
        return address.city;
      case AddressField.HOUSE_NUMBER:
        return address.houseNumber;
      case AddressField.STREET:
        return [address.dependantstreet, address.street].filter(Boolean).join(', ');
      case AddressField.PROVINCE:
        return address.province;
      case AddressField.COUNTRY:
        return address.country;
      case AddressField.ADDRESS_LINE_1:
        return [address.subbuilding, address.building].filter(Boolean).join(', ');
      case AddressField.ADDRESS_LINE_2:
        return address.addressLine3;
      case AddressField.POST_BOX:
        return address.pobox;
    }
  }

  getAsStringWithOptionalFields(
    address: CustomerAddress,
    market: string | undefined = address.country,
    includeProvince = true,
  ): string {
    return this.getAsStringInternal(address, true, market, ',\n', includeProvince);
  }

  getAsString(address: CustomerAddress, market: string | undefined = address.country, includeProvince = true): string {
    return this.getAsStringInternal(address, false, market, ',\n', includeProvince);
  }

  getAsSingleLineStringWithOptionalFields(
    address: CustomerAddress,
    market: string | undefined = address.country,
    includeProvince = true,
  ): string {
    return this.getAsStringInternal(address, true, market, ', ', includeProvince);
  }

  getAsSingleLineString(
    address: CustomerAddress,
    market: string | undefined = address.country,
    includeProvince = true,
  ): string {
    return this.getAsStringInternal(address, false, market, ', ', includeProvince);
  }

  private getAsStringInternal(
    address: CustomerAddress,
    includeOptionalAddressFields = true,
    market: string | undefined = address.country,
    lineSeparator = ',\n',
    includeProvince = true,
  ): string {
    const format = this.getPrintFormatForMarket(market, includeOptionalAddressFields, includeProvince);
    return format
      .map((line) =>
        line
          .map((field) => this.getCustomerAddressFieldValue(field, address))
          .filter((value) => !!value)
          .join(' '),
      )
      .filter((line) => !!line)
      .join(lineSeparator);
  }

  getAllAddressFields(): AddressField[] {
    return Object.values(AddressField);
  }

  getProvinceName(provinceCode: string | undefined, country: string | undefined): string | undefined {
    if (!provinceCode) return provinceCode;
    const byCountry = country ? PROVINCE_NAMES[country.toUpperCase()] : undefined;
    // fall back to scanning all countries (codes are globally unique due to prefix), then to the raw code
    return (
      byCountry?.[provinceCode] ??
      Object.values(PROVINCE_NAMES).find((m) => provinceCode in m)?.[provinceCode] ??
      provinceCode
    );
  }

  getProvinceOptions(country: string | undefined): SelectListOption[] {
    const byCountry = country ? PROVINCE_NAMES[country.toUpperCase()] : undefined;
    if (!byCountry) return [];
    return Object.entries(byCountry).map(([code, name]) => ({ id: code, label: name }));
  }
}
